import React from 'react';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import DetailSection from 'components/DetailSection';

const PageContainer = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1f2937')};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#F1F5F9'};
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  overflow-x: auto;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin-top: 16px;
`;

const ContentWrapper = styled.div`
  padding: 24px;
`;

const DetailSectionPage = () => {
  return (
    <PageContainer>
      <Section>
        <SectionTitle>DetailSection Component</SectionTitle>
        <Description>
          DetailSection is a themed section component for detail pages. It
          provides a consistent layout with a title and content area, and
          includes helper subcomponents for creating grids of label/value items.
          Supports theme-aware styling for both light and dark modes.
        </Description>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <Description>
          Use DetailSection with DetailSection.Grid and DetailSection.Item to
          create a structured layout for displaying information.
        </Description>

        <Card>
          <ContentWrapper>
            <DetailSection title="User Information">
              <DetailSection.Grid>
                <DetailSection.Item label="Full Name" value="John Doe" />
                <DetailSection.Item label="Email" value="john@example.com" />
                <DetailSection.Item label="Phone" value="+1 (555) 123-4567" />
                <DetailSection.Item label="Location" value="New York, NY" />
              </DetailSection.Grid>
            </DetailSection>
          </ContentWrapper>
        </Card>

        <CodeBlock>
          {`import DetailSection from 'components/DetailSection';

<DetailSection title="User Information">
  <DetailSection.Grid>
    <DetailSection.Item label="Full Name" value="John Doe" />
    <DetailSection.Item label="Email" value="john@example.com" />
    <DetailSection.Item label="Phone" value="+1 (555) 123-4567" />
    <DetailSection.Item label="Location" value="New York, NY" />
  </DetailSection.Grid>
</DetailSection>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Custom Grid Columns</SectionTitle>
        <Description>
          Adjust the number of columns in the grid using the columns prop.
          Defaults to 2 columns on desktop and 1 on mobile.
        </Description>

        <Card>
          <ContentWrapper>
            <DetailSection title="Product Details">
              <DetailSection.Grid columns={3}>
                <DetailSection.Item label="SKU" value="PRD-001" />
                <DetailSection.Item label="Category" value="Electronics" />
                <DetailSection.Item label="Brand" value="TechCorp" />
                <DetailSection.Item label="Price" value="$299.99" />
                <DetailSection.Item label="Stock" value="142 units" />
                <DetailSection.Item label="Weight" value="1.2 kg" />
              </DetailSection.Grid>
            </DetailSection>
          </ContentWrapper>
        </Card>

        <CodeBlock>
          {`<DetailSection title="Product Details">
  <DetailSection.Grid columns={3}>
    <DetailSection.Item label="SKU" value="PRD-001" />
    <DetailSection.Item label="Category" value="Electronics" />
    <DetailSection.Item label="Brand" value="TechCorp" />
    <DetailSection.Item label="Price" value="$299.99" />
    <DetailSection.Item label="Stock" value="142 units" />
    <DetailSection.Item label="Weight" value="1.2 kg" />
  </DetailSection.Grid>
</DetailSection>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Custom Value Rendering</SectionTitle>
        <Description>
          Pass children to DetailSection.Item instead of value for custom
          rendering, such as badges, links, or formatted content.
        </Description>

        <Card>
          <ContentWrapper>
            <DetailSection title="Order Status">
              <DetailSection.Grid>
                <DetailSection.Item label="Order ID" value="#ORD-12345" />
                <DetailSection.Item label="Status">
                  <Badge variant="success" size="small">
                    Completed
                  </Badge>
                </DetailSection.Item>
                <DetailSection.Item label="Payment">
                  <Badge variant="info" size="small">
                    Paid
                  </Badge>
                </DetailSection.Item>
                <DetailSection.Item label="Shipping">
                  <Badge variant="warning" size="small">
                    In Transit
                  </Badge>
                </DetailSection.Item>
              </DetailSection.Grid>
            </DetailSection>
          </ContentWrapper>
        </Card>

        <CodeBlock>
          {`import Badge from 'components/Badge';

<DetailSection title="Order Status">
  <DetailSection.Grid>
    <DetailSection.Item label="Order ID" value="#ORD-12345" />
    <DetailSection.Item label="Status">
      <Badge variant="success" size="small">Completed</Badge>
    </DetailSection.Item>
    <DetailSection.Item label="Payment">
      <Badge variant="info" size="small">Paid</Badge>
    </DetailSection.Item>
    <DetailSection.Item label="Shipping">
      <Badge variant="warning" size="small">In Transit</Badge>
    </DetailSection.Item>
  </DetailSection.Grid>
</DetailSection>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Without Bottom Margin</SectionTitle>
        <Description>
          Use the noMargin prop when the DetailSection is the last element in a
          container to remove the bottom margin.
        </Description>

        <Card>
          <ContentWrapper>
            <DetailSection title="Account Settings" noMargin>
              <DetailSection.Grid>
                <DetailSection.Item label="Notifications" value="Enabled" />
                <DetailSection.Item label="Language" value="English" />
                <DetailSection.Item label="Timezone" value="EST (UTC-5)" />
                <DetailSection.Item label="Theme" value="Dark Mode" />
              </DetailSection.Grid>
            </DetailSection>
          </ContentWrapper>
        </Card>

        <CodeBlock>
          {`<DetailSection title="Account Settings" noMargin>
  <DetailSection.Grid>
    <DetailSection.Item label="Notifications" value="Enabled" />
    <DetailSection.Item label="Language" value="English" />
  </DetailSection.Grid>
</DetailSection>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props Reference</SectionTitle>

        <Description>
          <strong>DetailSection Props:</strong>
        </Description>
        <CodeBlock>
          {`{
  title?: string,       // Section title (optional)
  children: node,       // Section content
  noMargin?: boolean,   // Remove bottom margin (default: false)
  className?: string,   // Additional className
}`}
        </CodeBlock>

        <Description style={{ marginTop: '24px' }}>
          <strong>DetailSection.Grid Props:</strong>
        </Description>
        <CodeBlock>
          {`{
  children: node,       // DetailSection.Item components
  columns?: number,     // Number of columns (default: 2)
  gap?: string,         // Gap between items (default: "16px")
}`}
        </CodeBlock>

        <Description style={{ marginTop: '24px' }}>
          <strong>DetailSection.Item Props:</strong>
        </Description>
        <CodeBlock>
          {`{
  label: string,        // Label text (required)
  value?: string | number | node,  // Value to display
  children?: node,      // Custom content instead of value
}`}
        </CodeBlock>
      </Section>
    </PageContainer>
  );
};

export default DetailSectionPage;
